export const reviewFieldHelp = {
  review_email_enabled: "Send feedback requests automatically after tickets close.",
  google_review_url:
    "Optional: shown to clients who leave positive feedback (public review link).",
  feedback_first_enabled:
    "Recommended: collect feedback on the Ellyanna page before redirecting to a public review.",
  review_notify_email:
    "Alerts for 3-star and below feedback are sent to this email.",
  review_send_mode: "Choose whether review emails go out immediately or after a delay.",
  review_send_delay_minutes: "Delay review emails by this many minutes when delayed mode is enabled.",
  review_quiet_hours_enabled: "Hold review emails during quiet hours.",
  review_quiet_start_local: "Quiet hours start time (local salon time).",
  review_quiet_end_local: "Quiet hours end time (local salon time).",
  review_reminder_enabled: "Send reminder emails if clients do not respond.",
  review_reminder_delay_days: "Days after the initial email to send a reminder.",
  review_reminder_max_count: "Maximum number of reminder emails per request.",
  review_kpi_min_reviews: "Minimum reviews required to show technician KPI.",
  review_kpi_window_days: "Rolling window size for technician KPI calculations.",
  review_kpi_weight_overall: "KPI weight for overall rating.",
  review_kpi_weight_service: "KPI weight for service rating.",
  review_kpi_weight_staff: "KPI weight for staff rating.",
  review_followup_enabled: "Create follow-up tasks for low ratings.",
  review_followup_threshold: "Overall rating threshold that triggers a follow-up.",
  review_followup_default_assignee_user_id: "Default assignee for new follow-ups.",
  followup_sla_enabled:
    "Set a time goal for responding to low ratings, and optionally send reminders if follow-ups are overdue.",
  followup_sla_hours: "Time goal to respond after a low rating (in hours).",
  followup_overdue_reminder_hours:
    "Send overdue reminder after this many hours.",
  followup_auto_assign_enabled:
    "Automatically assign follow-ups to the default assignee when available.",
  followup_manager_notify_email:
    "Fallback email for overdue reminders when no assignee is set.",
  public_review_ask_enabled:
    "Offer a public review link when feedback meets your threshold.",
  public_review_ask_threshold: "Minimum rating required to show the public ask.",
  public_review_ask_mode:
    "Choose immediate ask on the thank-you screen or delayed email.",
  public_review_ask_delay_hours:
    "Delay in hours before sending the public review ask email.",
  public_review_ask_require_comment:
    "Require a comment before showing the public review ask.",
  public_review_ask_min_feedback_length:
    "Minimum feedback length for the public review ask.",
  public_review_ask_variant_a_subject: "Subject for the public review ask email.",
  public_review_ask_variant_a_body: "Body for the public review ask email.",
  csat_goal_enabled:
    "Track your average star rating and review volume goals on reports.",
  csat_goal_target_rating: "Target average rating (stars).",
  csat_goal_target_reviews_per_week:
    "Target number of reviews per week.",
  review_email_template_enabled:
    "Toggle on to customize the subject and message sent to clients.",
  review_email_subject: "Subject line for the review request email.",
  review_email_body: "Message body for the review request email.",
};

export const reviewTooltips = {
  review_email_enabled:
    "Turns on automatic review request emails after a ticket is closed.",
  review_send_mode:
    "Choose whether review emails send right away or after a delay.",
  review_send_delay_minutes:
    "How many minutes to wait before sending the review email (only used in delayed mode).",
  review_quiet_hours_enabled:
    "Hold review emails during quiet hours so clients do not get messages late at night.",
  review_quiet_start_local: "Quiet hours start time (local salon time).",
  review_quiet_end_local: "Quiet hours end time (local salon time).",
  review_reminder_enabled:
    "Send reminder emails if a client does not respond.",
  review_reminder_delay_days:
    "Days after the first email to send the first reminder.",
  review_reminder_max_count: "Maximum number of reminder emails to send.",
  google_review_url: "Where clients will be taken to leave a public review.",
  feedback_first_enabled:
    "If enabled, review_url will point to the Ellyanna feedback page.",
  review_notify_email:
    "Internal email to notify your salon about review activity or follow-ups.",
  review_kpi_min_reviews: "Used for performance tracking and analytics goals.",
  review_kpi_window_days: "Used for performance tracking and analytics goals.",
  review_kpi_weight_overall:
    "How much this score contributes to the overall KPI total.",
  review_kpi_weight_service:
    "How much this score contributes to the overall KPI total.",
  review_kpi_weight_staff:
    "How much this score contributes to the overall KPI total.",
  review_followup_enabled:
    "Create a follow-up alert when a review is below your chosen rating.",
  review_followup_threshold:
    "If a rating is below this number, Ellyanna will flag it for follow-up.",
  review_followup_default_assignee_user_id:
    "Who should handle negative review follow-ups by default.",
  followup_sla_enabled:
    "Set a time goal for responding to low ratings, and optionally send reminders if follow-ups are overdue.",
  followup_sla_hours:
    "How many hours you aim to respond after a low rating is received.",
  followup_overdue_reminder_hours:
    "If a follow-up is still not completed, send a reminder after this many hours.",
  followup_auto_assign_enabled:
    "Auto-assign to the default follow-up owner.",
  followup_manager_notify_email:
    "Fallback inbox for overdue reminders.",
  public_review_ask_enabled:
    "Offer a public review request for positive feedback.",
  public_review_ask_threshold: "Minimum rating to show the public ask.",
  public_review_ask_mode: "Immediate means on-screen, delayed means email.",
  public_review_ask_delay_hours: "Delay before sending the public ask email.",
  public_review_ask_require_comment:
    "Require a comment to unlock the public ask.",
  public_review_ask_min_feedback_length:
    "Minimum comment length to unlock the public ask.",
  public_review_ask_variant_a_subject:
    "Subject line for the public review ask email.",
  public_review_ask_variant_a_body:
    "Message body for the public review ask email.",
  csat_goal_enabled: "Show customer satisfaction goals in the report view.",
  csat_goal_target_rating:
    "Your goal for the average star rating (for example, 4.6 out of 5).",
  csat_goal_target_reviews_per_week:
    "Your goal for how many reviews you want to collect each week.",
  review_email_template_enabled:
    "When off, Ellyanna uses the default message.",
  review_email_subject: "Customize the subject line clients see.",
  review_email_body: "Customize the message clients see.",
  review_email_tokens: "Click to copy a token or insert it into your message.",
  review_email_preview: "This is a preview with sample data.",
};

export const reviewSetupGuideSteps = [
  {
    title: "Turn on review emails",
    body:
      "Enable \"Review emails\" to allow Ellyanna to send review requests to clients after a ticket is closed.",
  },
  {
    title: "Choose when to send",
    body: "Use \"Send mode\":",
    lines: [
      "\"On ticket close\" sends immediately when the ticket is closed.",
      "\"Delayed\" sends after a delay (set the delay minutes).",
    ],
  },
  {
    title: "Set quiet hours (recommended)",
    body:
      "Turn on \"Quiet hours\" to prevent review emails from sending late at night.",
    lines: [
      "Choose a \"Quiet start\" and \"Quiet end\" (local salon time).",
    ],
  },
  {
    title: "Optional: Turn on reminders",
    body:
      "Enable reminders if you want Ellyanna to follow up when a client does not respond.",
    lines: [
      "Set the \"Reminder delay (days)\" and \"Max reminders\".",
    ],
  },
  {
    title: "Choose where clients will leave a review",
    body:
      "Add your \"Public review URL\" (for example, your review page URL).",
  },
  {
    title: "Turn on feedback-first (recommended)",
    body:
      "Enable Feedback-first so clients leave feedback on the Ellyanna page before being redirected to your public review link.",
  },
  {
    title: "Choose who gets notified",
    body:
      "Add \"Review alert email\" so the salon receives internal notifications when reviews come in or when follow-up is needed.",
  },
  {
    title: "Set follow-up rules (recommended)",
    body:
      "Turn on \"Follow-up\" to alert the salon when a review is below your chosen rating threshold.",
    lines: [
      "Pick a default staff member to handle follow-ups.",
    ],
  },
  {
    title: "Save and test",
    body: "Click \"Save Changes\".",
    lines: [
      "Close a test ticket to confirm the timing and that quiet hours behave correctly.",
    ],
  },
];

export const reviewSetupGuideNote =
  "All times are based on your salon timezone from your salon setup.";
